import { Clock, Lightbulb, Percent, TrendingUp } from "lucide-react";

import { CountryCode } from "@krea/shared/types/common";

import {
  BidTypeEnum,
  OfferAttractivenessRecommendationStatusEnum,
} from "@/features/common/enums";

/**
 * Contact for anything Offer Attractiveness — access requests and keeping it after the
 * free trial. A named owner per market rather than the general support inbox from
 * `getCompanyDetails`, shared by both modals so they stay in step. (KREA-4468)
 */
const OFFER_ATTRACTIVENESS_CONTACT_EMAILS: Record<CountryCode, string> = {
  [CountryCode.SE]: "ella.almstedt@krea.se",
  [CountryCode.FI]: "olli.lappi@kredita.fi",
};

export function getOfferAttractivenessContactEmail(
  countryCode: CountryCode | undefined,
): string {
  // Fall back to the Swedish owner rather than rendering an empty contact card if the
  // lender company has not loaded yet.
  return countryCode
    ? OFFER_ATTRACTIVENESS_CONTACT_EMAILS[countryCode]
    : OFFER_ATTRACTIVENESS_CONTACT_EMAILS[CountryCode.SE];
}

export function getQualityTitle(score: number): string {
  if (score < 2) return "Low likelihood to convert";
  if (score < 5) return "Medium likelihood to convert";
  if (score < 9) return "High likelihood to convert";

  return "Very high likelihood to convert";
}

export function getRecommendationIcon(
  status: OfferAttractivenessRecommendationStatusEnum,
) {
  const iconClass = "size-3 text-primary";

  switch (status) {
    case OfferAttractivenessRecommendationStatusEnum.INCREASE_AMOUNT:
      return <TrendingUp className={iconClass} />;
    case OfferAttractivenessRecommendationStatusEnum.LOWER_COMPARABLE_INTEREST_RATE:
      return <Percent className={iconClass} />;
    case OfferAttractivenessRecommendationStatusEnum.INCREASE_REPAYMENT_TIME:
      return <Clock className={iconClass} />;
    default:
      return <Lightbulb className={iconClass} />;
  }
}

export function getAttractivenessColor(
  score: number,
  style: "number" | "gauge",
) {
  if (style === "number") {
    if (score <= 1) return "bg-red-500";
    if (score <= 4) return "bg-orange-500";
    if (score <= 8) return "bg-mint-400";
  }
  if (score < 1) return "bg-red-500";
  if (score < 4) return "bg-orange-500";
  if (score < 8) return "bg-mint-400";

  return "bg-mint-500";
}

export function getAlertVariant(score: number | null | undefined) {
  if (score === null || score === undefined) return "info";
  if (score <= 1) return "critical";
  if (score <= 4) return "warning";

  return "success";
}

export function getRecommendationDisplayText(
  status: OfferAttractivenessRecommendationStatusEnum,
  bidType: BidTypeEnum | undefined,
  apiStatusText: string,
): string {
  switch (status) {
    case OfferAttractivenessRecommendationStatusEnum.INCREASE_AMOUNT:
      return bidType === BidTypeEnum.FACTORING
        ? "Increase the purchase limit"
        : apiStatusText;
    case OfferAttractivenessRecommendationStatusEnum.INCREASE_REPAYMENT_TIME:
      if (bidType === BidTypeEnum.REAL_ESTATE) return "Increase the loan term";

      if (bidType === BidTypeEnum.REVOLVING_CREDIT) {
        return "Lower the amortisation per month";
      }

      return apiStatusText;
    default:
      return apiStatusText;
  }
}
